import React, { useEffect, useState } from "react";
import api, { asArray } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import useReveal from "../hooks/useReveal";
import CountUp from "../components/CountUp";
import * as LucideIcons from "lucide-react";

const RIYADH_HERO = "https://images.unsplash.com/photo-1663900108404-a05e8bf82cda?fm=jpg&q=85&w=2400&auto=format&fit=crop";

const VALUES_AR = [
    { title: "النزاهة أولاً", body: "التزام بالشفافية والشجاعة في التواصل لتعزيز الثقة مع شركائنا وعملائنا" },
    { title: "علاقات حقيقية", body: "نبني جسور التفاهم عبر حوار شامل وفكر مبتكر للنمو المشترك" },
    { title: "رشاقة ديناميكية", body: "نقتنص الفرص الناشئة باستراتيجيات سريعة ومرنة تستبق التغيرات" },
    { title: "رؤية شاملة", body: "نرفع المعايير مع كل إنجاز ونقدم حلولاً تصنع نجاحاً طويل الأمد" },
    { title: "قيادة حكيمة", body: "نلهم التغيير ونقدّم مفاهيم سوقية مبتكرة تنسجم مع الأهداف" },
    { title: "بحث متواصل", body: "نُعمّق خبرتنا عبر سعي دائم للمعرفة وتقديم نتائج بدقة وتميّز" },
];
const VALUES_EN = [
    { title: "Integrity First", body: "Commitment to transparency and courage in communication, fostering trust with clients and partners" },
    { title: "Genuine Relationships", body: "Building bridges of understanding through inclusive dialogue and innovative thinking for mutual growth" },
    { title: "Dynamic Agility", body: "Seizing emerging opportunities and anticipating change with swift, flexible strategies" },
    { title: "Comprehensive Vision", body: "Raising standards with every achievement, delivering solutions that shape long-term success" },
    { title: "Wise Leadership", body: "Inspiring change and introducing innovative market concepts aligned with defined objectives" },
    { title: "Continuous Pursuit", body: "Deepening expertise through a constant quest for knowledge, delivering exceptional clarity and distinction" },
];

export default function AboutPage() {
    const { t, lang, pick } = useI18n();
    const ref = useReveal();
    const [stats, setStats] = useState([]);
    const [values, setValues] = useState([]);

    useEffect(() => {
        api.get("/stats").then((r) => setStats(asArray(r.data, "stats").filter((s) => !(s.category || "").toLowerCase().includes("influencer")))).catch(() => {});
        api.get("/values").then((r) => setValues(asArray(r.data, "values"))).catch(() => {});
    }, []);

    // Fallback hardcoded values only if API returns empty (e.g. fresh install before seed)
    const displayValues = values.length > 0
        ? values
        : (lang === "ar" ? VALUES_AR : VALUES_EN).map((v, i) => ({ id: `fb-${i}`, title_ar: v.title, body_ar: v.body, title_en: v.title, body_en: v.body, icon: "Plus" }));
    const aboutParagraphs = t("section_about_body")
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);

    return (
        <div ref={ref} data-testid="about-page">
            {/* HERO: Riyadh nightscape with deep blue overlay (matches company profile "مسار أسطوري" page) */}
            <section className="relative min-h-[90vh] overflow-hidden bg-[#002951] text-white -mt-20 pt-20">
                <img
                    src={RIYADH_HERO}
                    alt="Riyadh skyline at night"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 animate-slow-zoom"
                />
                {/* Royal blue / violet gradient overlay matching the PDF aesthetic */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#002951]/88 via-[#0060A7]/58 to-[#002951]/75" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#00213f] via-transparent to-transparent" />
                <div className="grain-overlay" />

                <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 min-h-[80vh] grid grid-cols-12 items-center py-20">
                    <div className="col-span-12 lg:col-span-7 lg:col-start-6 reveal">
                        <div className="section-eyebrow section-eyebrow-dark mb-6">
                            {t("section_about_overline")}
                        </div>
                        <h1 className="heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] mb-10">
                            {t("section_about_title")}
                        </h1>
                        <div className="space-y-5 text-base md:text-lg leading-relaxed text-white/85 max-w-2xl">
                            {aboutParagraphs.map((paragraph, i) => (
                                <p key={i}>{paragraph}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* NETWORK / NUMBERS */}
            <section className="bg-[#002951] text-white py-20 md:py-28 relative overflow-hidden">
                <div className="grain-overlay" />
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
                    <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3 plus-mark">
                        {t("section_numbers_overline")}
                    </div>
                    <h2 className="heading page-hero-oneline mb-14 reveal">
                        {t("section_numbers_title")}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-14">
                        {stats.map((s, i) => {
                            const Icon = LucideIcons[s.icon] || LucideIcons.Sparkles;
                            return (
                                <div key={s.id || i} className="text-center md:text-start">
                                    <Icon size={42} strokeWidth={1.3} className="text-[#81D3EB] mb-5 mx-auto md:mx-0" />
                                    <div className="heading text-5xl md:text-6xl tracking-tight font-black leading-none">
                                        <CountUp value={s.value} duration={2200} />
                                    </div>
                                    <div className="text-sm md:text-base text-white/70 mt-3">{pick(s, "label")}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="bg-[#EFF0F1] pt-20 pb-14 md:pt-24 md:pb-16">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#6B7280] mb-3 plus-mark reveal">
                        {isRTL ? "قيمنا" : "Our Values"}
                    </div>
                    <h2 className="heading page-hero-oneline mb-14 reveal">
                        {isRTL ? "مبادئ تقود قراراتنا" : "Principles guiding decisions"}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
                        {displayValues.map((v, i) => {
                            const Icon = LucideIcons[v.icon] || LucideIcons.Plus;
                            return (
                                <div key={v.id || i} className="bg-[#EFF0F1] p-8 reveal" data-testid={`value-${i}`}>
                                    <Icon size={20} strokeWidth={2.2} className="text-[#0060A7] mb-5" />
                                    <h3 className="heading text-xl tracking-tight mb-3 font-bold">{pick(v, "title")}</h3>
                                    <p className="text-[#374151] text-base leading-relaxed">{pick(v, "body")}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}
